<template>
  <div v-if="isVisible" class="full-page-loading" :class="{ 'fade-out': isFadingOut }">
    <!-- Blur Background -->
    <div class="blur-background" :class="blurClass"></div>
    
    <!-- Loading Content -->
    <div class="loading-content">
      <!-- Logo/Brand -->
      <div class="loading-logo">
        <img 
          v-if="logo" 
          :src="logo" 
          :alt="brandName" 
          class="logo-image"
        />
        <div v-else class="logo-placeholder">
          <v-icon size="64" :color="iconColor">{{ brandIcon }}</v-icon>
        </div>
      </div>

      <!-- Brand Name -->
      <h1 class="brand-name" :class="textClass">{{ brandName }}</h1>

      <!-- Loading Animation -->
      <div class="loading-animation">
        <div class="spinner-container">
          <!-- Circular Spinner -->
          <div v-if="spinnerType === 'circular'" class="spinner circular" :class="spinnerClass">
            <div class="spinner-inner"></div>
          </div>
          
          <!-- Dots Spinner -->
          <div v-else-if="spinnerType === 'dots'" class="spinner dots">
            <div class="dot" :class="dotClass"></div>
            <div class="dot" :class="dotClass"></div>
            <div class="dot" :class="dotClass"></div>
          </div>
          
          <!-- Pulse Spinner -->
          <div v-else-if="spinnerType === 'pulse'" class="spinner pulse" :class="pulseClass"></div>
          
          <!-- Wave Spinner -->
          <div v-else-if="spinnerType === 'wave'" class="spinner wave">
            <div class="wave-bar" :class="waveClass"></div>
            <div class="wave-bar" :class="waveClass"></div>
            <div class="wave-bar" :class="waveClass"></div>
            <div class="wave-bar" :class="waveClass"></div>
            <div class="wave-bar" :class="waveClass"></div>
          </div>
          
          <!-- Ring Spinner -->
          <div v-else-if="spinnerType === 'ring'" class="spinner ring" :class="ringClass">
            <div class="ring-inner"></div>
          </div>
          
          <!-- Default Circular -->
          <div v-else class="spinner circular" :class="spinnerClass">
            <div class="spinner-inner"></div>
          </div>
        </div>
      </div>

      <!-- Loading Text -->
      <div class="loading-text">
        <p class="main-text" :class="textClass">{{ currentText }}</p>
        <p v-if="subText" class="sub-text" :class="subTextClass">{{ subText }}</p>
      </div>

      <!-- Progress Bar -->
      <div v-if="showProgress && progress !== null" class="progress-container">
        <div class="progress-bar" :class="progressBarClass">
          <div 
            class="progress-fill" 
            :class="progressFillClass"
            :style="{ width: progress + '%' }"
          ></div>
        </div>
        <span class="progress-text" :class="textClass">{{ progress }}%</span>
      </div>

      <!-- Loading Steps -->
      <div v-if="steps && steps.length > 0" class="loading-steps">
        <div 
          v-for="(step, index) in steps" 
          :key="index"
          class="step-item"
          :class="{ 
            'completed': index < currentStep, 
            'active': index === currentStep,
            'pending': index > currentStep
          }"
        >
          <div class="step-icon" :class="stepIconClass(index)">
            <v-icon v-if="index < currentStep" size="16" color="success">mdi-check</v-icon>
            <v-icon v-else-if="index === currentStep" size="16" :color="iconColor">mdi-loading</v-icon>
            <span v-else class="step-number" :class="textClass">{{ index + 1 }}</span>
          </div>
          <span class="step-text" :class="textClass">{{ step }}</span>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
export default {
  name: 'FullPageLoading',
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    brandName: {
      type: String,
      default: 'Wasata'
    },
    brandIcon: {
      type: String,
      default: 'mdi-home'
    },
    logo: {
      type: String,
      default: null
    },
    loadingText: {
      type: String,
      default: 'جاري التحميل...'
    },
    subText: {
      type: String,
      default: ''
    },
    spinnerType: {
      type: String,
      default: 'circular',
      validator: value => ['circular', 'dots', 'pulse', 'wave', 'ring'].includes(value)
    },
    progress: {
      type: Number,
      default: null
    },
    showProgress: {
      type: Boolean,
      default: false
    },
    steps: {
      type: Array,
      default: () => []
    },
    currentStep: {
      type: Number,
      default: 0
    },
    autoHide: {
      type: Boolean,
      default: false
    },
    autoHideDelay: {
      type: Number,
      default: 3000
    },
    isDark: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isFadingOut: false,
      currentText: this.loadingText,
      textIndex: 0,
      loadingTexts: [
        'جاري التحميل...',
        'جاري التهيئة...',
        'جاري تحضير البيانات...',
        'تقريباً جاهز...'
      ]
    };
  },
  computed: {
    blurClass() {
      return this.isDark ? 'dark-blur' : 'light-blur';
    },
    textClass() {
      return this.isDark ? 'dark-text' : 'light-text';
    },
    subTextClass() {
      return this.isDark ? 'dark-sub-text' : 'light-sub-text';
    },
    iconColor() {
      return this.isDark ? 'white' : 'primary';
    },
    spinnerClass() {
      return this.isDark ? 'dark-spinner' : 'light-spinner';
    },
    dotClass() {
      return this.isDark ? 'dark-dot' : 'light-dot';
    },
    pulseClass() {
      return this.isDark ? 'dark-pulse' : 'light-pulse';
    },
    waveClass() {
      return this.isDark ? 'dark-wave' : 'light-wave';
    },
    ringClass() {
      return this.isDark ? 'dark-ring' : 'light-ring';
    },
    progressBarClass() {
      return this.isDark ? 'dark-progress-bar' : 'light-progress-bar';
    },
    progressFillClass() {
      return this.isDark ? 'dark-progress-fill' : 'light-progress-fill';
    },
    bgElementClass() {
      return this.isDark ? 'dark-bg-element' : 'light-bg-element';
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.startLoading();
      } else {
        this.fadeOut();
      }
    },
    loadingText(newVal) {
      this.currentText = newVal;
    }
  },
  mounted() {
    if (this.isVisible) {
      this.startLoading();
    }
  },
  methods: {
    startLoading() {
      this.isFadingOut = false;
      this.currentText = this.loadingText;
      
      if (this.loadingTexts.length > 1) {
        this.startTextRotation();
      }
      
      if (this.autoHide) {
        setTimeout(() => {
          this.hide();
        }, this.autoHideDelay);
      }
    },
    
    startTextRotation() {
      const interval = setInterval(() => {
        if (!this.isVisible) {
          clearInterval(interval);
          return;
        }
        
        this.textIndex = (this.textIndex + 1) % this.loadingTexts.length;
        this.currentText = this.loadingTexts[this.textIndex];
      }, 1500);
    },
    
    fadeOut() {
      this.isFadingOut = true;
      setTimeout(() => {
        this.$emit('hidden');
      }, 500);
    },
    
    hide() {
      this.$emit('hide');
    },
    
    stepIconClass(index) {
      if (index < this.currentStep) {
        return this.isDark ? 'dark-completed' : 'light-completed';
      } else if (index === this.currentStep) {
        return this.isDark ? 'dark-active' : 'light-active';
      } else {
        return this.isDark ? 'dark-pending' : 'light-pending';
      }
    }
  }
};
</script>

<style scoped>
.full-page-loading {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  opacity: 1;
  transition: opacity 0.5s ease;
}

.full-page-loading.fade-out {
  opacity: 0;
}

/* Blur Background */
.blur-background {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  transition: all 0.3s ease;
}

.dark-blur {

  background: rgba(0, 0, 0, 0.8);
  box-shadow: inset 0 0 100px rgba(0, 0, 0, 0.5);
}

.light-blur {
  background: rgba(255, 255, 255, 0.8);
  box-shadow: inset 0 0 100px rgba(255, 255, 255, 0.3);
}
/* 
  Use these two classes specifically when changing theme mode (light <-> dark).
  They provide a smooth transition effect for the blur background.
*/
.dark-blur--switch-theme {
  background: rgba(255, 255, 255, 0.8) !important;
  box-shadow: inset 0 0 100px rgba(255, 255, 255, 0.3) !important;
  transition: background 0.4s, box-shadow 0.4s;
}

.light-blur--switch-theme {
  background: rgba(0, 0, 0, 0.8) !important;
  box-shadow: inset 0 0 100px rgba(0, 0, 0, 0.5) !important;
  transition: background 0.4s, box-shadow 0.4s;
}
/* Loading Content */
.loading-content {
  text-align: center;
  z-index: 2;
  position: relative;
  padding: 40px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.dark .loading-content {
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
}

/* Logo */
.loading-logo {
  margin-bottom: 30px;
}

.logo-image {
  max-width: 100px;
  max-height: 100px;
  border-radius: 50%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.logo-placeholder {
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.dark .logo-placeholder {
  background: rgba(255, 255, 255, 0.05);
}

/* Brand Name */
.brand-name {
  font-size: 3rem;
  font-weight: 300;
  margin: 0 0 40px 0;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
}

.light-text {
  color: #6a6a6a;
}

.dark-text {
  color: #f3f5f7;
}

/* Loading Animation */
.loading-animation {
  margin: 40px 0;
}

.spinner-container {
  display: flex;
  justify-content: center;
  align-items: center;
}

/* Circular Spinner */
.spinner.circular {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  position: relative;
}

.light-spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-top: 4px solid #8219d2;
}

.dark-spinner {
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-top: 4px solid #8219d2;
}

.spinner-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: spin 1s linear infinite;
}

/* Dots Spinner */
.spinner.dots {
  display: flex;
  gap: 12px;
}

.dot {
  width: 16px;
  height: 16px;
  border-radius: 50%;
  animation: bounce 1.4s ease-in-out infinite both;
}

.light-dot {
  background: #8219d2;
}

.dark-dot {
  background: #8219d2;
}

.dot:nth-child(1) { animation-delay: -0.32s; }
.dot:nth-child(2) { animation-delay: -0.16s; }
.dot:nth-child(3) { animation-delay: 0s; }

/* Pulse Spinner */
.spinner.pulse {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

.light-pulse {
  background: #8219d2;
}

.dark-pulse {
  background: #8219d2;
}

/* Wave Spinner */
.spinner.wave {
  display: flex;
  gap: 6px;
  align-items: end;
}

.wave-bar {
  width: 8px;
  border-radius: 4px;
  animation: wave 1.2s ease-in-out infinite;
}

.light-wave {
  background: #8219d2;
}

.dark-wave {
  background: #8219d2;
}

.wave-bar:nth-child(1) { height: 20px; animation-delay: 0s; }
.wave-bar:nth-child(2) { height: 30px; animation-delay: 0.1s; }
.wave-bar:nth-child(3) { height: 40px; animation-delay: 0.2s; }
.wave-bar:nth-child(4) { height: 30px; animation-delay: 0.3s; }
.wave-bar:nth-child(5) { height: 20px; animation-delay: 0.4s; }

/* Ring Spinner */
.spinner.ring {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  position: relative;
}

.light-ring {
  border: 3px solid rgba(0, 0, 0, 0.1);
  border-top: 3px solid #1976d2;
  border-right: 3px solid #1976d2;
}

.dark-ring {
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid #8219d2;
  border-right: 3px solid #8219d2;
}

.ring-inner {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  animation: spin 1.2s linear infinite;
}

/* Loading Text */
.loading-text {
  margin: 30px 0;
}

.main-text {
  font-size: 1.4rem;
  font-weight: 500;
  margin: 0 0 12px 0;
  opacity: 0.9;
}

.sub-text {
  font-size: 1rem;
  opacity: 0.7;
  margin: 0;
}

.light-sub-text {
  color: #666;
}

.dark-sub-text {
  color: #b0b3b8;
}

/* Progress Bar */
.progress-container {
  margin: 40px 0;
  width: 350px;
  margin-left: auto;
  margin-right: auto;
}

.progress-bar {
  width: 100%;
  height: 6px;
  border-radius: 3px;
  overflow: hidden;
  margin-bottom: 12px;
}

.light-progress-bar {
  background: rgba(0, 0, 0, 0.1);
}

.dark-progress-bar {
  background: rgba(255, 255, 255, 0.1);
}

.progress-fill {
  height: 100%;
  border-radius: 3px;
  transition: width 0.3s ease;
}

.light-progress-fill {
  background: linear-gradient(90deg, #1976d2, #42a5f5);
}

.dark-progress-fill {
  background: linear-gradient(90deg, #8219d2, #90caf9);
}

.progress-text {
  font-size: 1rem;
  opacity: 0.8;
}

/* Loading Steps */
.loading-steps {
  margin: 40px 0;
  text-align: left;
  max-width: 350px;
  margin-left: auto;
  margin-right: auto;
}

.step-item {
  display: flex;
  align-items: center;
  margin: 16px 0;
  opacity: 0.6;
  transition: opacity 0.3s ease;
}

.step-item.active {
  opacity: 1;
}

.step-item.completed {
  opacity: 0.8;
}

.step-icon {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;
  flex-shrink: 0;
}

.light-pending {
  background: rgba(0, 0, 0, 0.1);
}

.dark-pending {
  background: rgba(255, 255, 255, 0.1);
}

.light-active {
  background: rgba(25, 118, 210, 0.2);
}

.dark-active {
  background: rgba(100, 181, 246, 0.2);
}

.light-completed {
  background: rgba(76, 175, 80, 0.8);
}

.dark-completed {
  background: rgba(76, 175, 80, 0.6);
}

.step-number {
  font-size: 0.9rem;
  font-weight: 600;
}

.step-text {
  font-size: 1rem;
}

/* Background Elements */
.background-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;
  z-index: 1;
}

.bg-circle, .bg-square {
  position: absolute;
  border-radius: 50%;
  animation: float 6s ease-in-out infinite;
}

.bg-circle {
  border-radius: 50%;
}

.bg-square {
  border-radius: 20%;
}

.light-bg-element {
  background: rgba(25, 118, 210, 0.1);
}

.dark-bg-element {
  background: rgba(100, 181, 246, 0.1);
}

.circle-1 {
  width: 200px;
  height: 200px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.circle-2 {
  width: 150px;
  height: 150px;
  top: 60%;
  right: 15%;
  animation-delay: 2s;
}

.circle-3 {
  width: 100px;
  height: 100px;
  bottom: 20%;
  left: 20%;
  animation-delay: 4s;
}

.square-1 {
  width: 120px;
  height: 120px;
  top: 30%;
  right: 30%;
  animation-delay: 1s;
}

.square-2 {
  width: 80px;
  height: 80px;
  bottom: 30%;
  right: 10%;
  animation-delay: 3s;
}

/* Animations */
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.7; }
}

@keyframes wave {
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
}

@keyframes float {
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  33% { transform: translateY(-20px) rotate(120deg); }
  66% { transform: translateY(10px) rotate(240deg); }
}

/* Responsive Design */
@media (max-width: 768px) {
  .brand-name {
    font-size: 2.5rem;
  }
  
  .loading-content {
    padding: 30px 20px;
    margin: 0 20px;
  }
  
  .progress-container {
    width: 280px;
  }
  
  .loading-steps {
    max-width: 280px;
  }
  
  .logo-image, .logo-placeholder {
    max-width: 80px;
    max-height: 80px;
  }
}

@media (max-width: 480px) {
  .brand-name {
    font-size: 2rem;
  }
  
  .loading-content {
    padding: 20px 15px;
    margin: 0 15px;
  }
  
  .progress-container {
    width: 250px;
  }
  
  .loading-steps {
    max-width: 250px;
  }
  
  .logo-image, .logo-placeholder {
    max-width: 60px;
    max-height: 60px;
  }
  
  .spinner.circular, .spinner.pulse, .spinner.ring {
    width: 50px;
    height: 50px;
  }
}
</style>
